// ===== Logout automatico por inatividade (2h no computador, 7 dias no celular) =====
// Por que aqui no front e nao no Supabase: o recurso nativo de "Inactivity
// timeout" do Supabase Auth e SO de plano pago. Doc oficial (Auth > Sessions,
// secao "Limiting session lifetime and number of allowed sessions per user"):
//   https://supabase.com/docs/guides/auth/sessions#limiting-session-lifetime-and-number-of-allowed-sessions-per-user
//   "This feature is only available on Pro Plans and up."
// Este projeto era plano gratuito quando isto foi escrito. Em 29/10/2026 a organizacao da PRODUCAO (BeamCRM) esta no plano PRO (lido pelo conector do Supabase), entao os limites do servidor ja sao configuraveis; mesmo assim, no painel da producao (Authentication > Sessions), "Time-box user sessions" e "Inactivity timeout" seguem em 0 (nunca), conferido pelo usuario em 29/10: a regra de 2h/7 dias fica no front. Se forem ligados um dia, valem para o PROJETO inteiro (a
// pagina nao diz que da para dar um valor por aparelho). Como o computador e o celular tem prazos diferentes, a regra segue aqui no front; ligar um
// limite no servidor como rede de protecao ficou como decisao futura (spec 2026-10-27-sessao-de-7-dias-no-celular, D-S1: o Kaio disse nao por ora).
//
// A mesma doc diz que, por padrao, "all sessions are active until the user signs
// out" e que os refresh tokens "never expire" -- por isso, sem isto, a sessao
// dura ate o usuario clicar em Sair. Este modulo encerra a sessao (escopo local,
// so este navegador) depois de 2h sem atividade no computador e de 7 dias no celular (aparelho de toque; ver limiteDoAparelho).
//
// Detalhe que resolve o caso relatado (navegador reaberto depois de horas ainda
// logado): a ultima atividade e gravada no localStorage, que SOBREVIVE a reload e
// ao fechamento do navegador. Um timer so em memoria zeraria a cada recarga e
// nunca pegaria o "fechei e voltei 3h depois". Aqui, ao reabrir, comparamos o
// relogio com a marca salva e, se passou do limite (2h ou 7 dias), deslogamos na hora.

import React from 'react';

export const LIMITE_MS = 2 * 60 * 60 * 1000; // 2 horas sem atividade -> sai
export const AVISO_MS = 60 * 1000;           // avisa 1 minuto antes

// 7 dias sem usar no celular (spec 2026-10-27-sessao-de-7-dias-no-celular). O servidor ja mantem a sessao: "By default, all sessions are active
// until the user signs out or performs some other action that terminates a session" (https://supabase.com/docs/guides/auth/sessions).
// Quem limita e este front. No Safari do iPhone o teto real continua sendo o da WebKit (7 dias de uso do Safari sem abrir o site).
export const LIMITE_CELULAR_MS = 7 * 24 * 60 * 60 * 1000;

// Aparelho de toque como ponteiro principal (celular e tablet): decisao do Kaio em 27/10. Qualquer falha cai no lado MAIS SEGURO (computador,
// 2 horas): o jsdom e navegadores antigos nao tem matchMedia, e uma resposta estranha nao pode soltar 7 dias.
export function ehCelular() {
  try {
    return typeof window !== 'undefined'
      && typeof window.matchMedia === 'function'
      && window.matchMedia('(pointer: coarse)').matches === true;
  } catch (e) { return false; }
}

export function limiteDeInatividade({ celular }) {
  return celular ? LIMITE_CELULAR_MS : LIMITE_MS;
}

export function limiteDoAparelho() {
  return limiteDeInatividade({ celular: ehCelular() });
}

// Texto para a tela ("2 horas", "7 dias"): o aviso de logout diz o prazo que de fato valeu.
export function rotuloDoLimite(limiteMs) {
  const dias = limiteMs / (24 * 60 * 60 * 1000);
  if (Number.isInteger(dias) && dias >= 1) return dias === 1 ? '1 dia' : `${dias} dias`;
  const horas = limiteMs / (60 * 60 * 1000);
  if (Number.isInteger(horas) && horas >= 1) return horas === 1 ? '1 hora' : `${horas} horas`;
  return `${Math.round(limiteMs / 60000)} minutos`;
}

const CHAVE_ATIVIDADE = 'beamcrm.ultimaAtividade';
const CHAVE_MOTIVO = 'beamcrm.motivoLogout';

// ---- Regra pura (testavel): estado a partir do tempo decorrido ----
// Devolve 'ativo' | 'avisar' | 'expirado'. Sem marca de atividade ainda,
// trata como 'ativo' (recem-chegou; quem chama grava a marca inicial).
export function avaliarInatividade(agora, ultimaAtividade, opcoes = {}) {
  const limite = opcoes.limite ?? LIMITE_MS;
  const aviso = opcoes.aviso ?? AVISO_MS;
  if (ultimaAtividade == null) return 'ativo';
  const decorrido = agora - ultimaAtividade;
  if (decorrido >= limite) return 'expirado';
  if (decorrido >= limite - aviso) return 'avisar';
  return 'ativo';
}

// ---- Persistencia da ultima atividade (localStorage: sobrevive a reload) ----
//
// A MARCA TEM DONO, e isso e conserto de 15/09, nao enfeite.
//
// O localStorage sobrevive ao logout, ao fechar do navegador e aos dias -- e
// `limparAtividade` estava exportada sem ninguem chamar. Resultado: quem voltasse
// mais de 2h depois da ultima atividade entrava pelo magic link e era expulso no
// primeiro `verificar()`, que roda imediatamente. Todo dia seguinte ao primeiro
// uso cai nesse caso.
//
// E o sintoma NAO parece inatividade: a pessoa acabou de clicar no link e volta
// para a tela de login. Foi relatado duas vezes como "o magic link parou de
// funcionar".
//
// Gravar o dono resolve pela raiz: o relogio de inatividade pertence a UMA
// sessao. Marca de outra sessao e como se nao houvesse marca -- que e o caso
// "recem-chegou", ja tratado como ativo.
export function marcarAtividade(agora = Date.now(), sessaoId = null) {
  try { localStorage.setItem(CHAVE_ATIVIDADE, JSON.stringify({ t: agora, s: sessaoId })); } catch (e) {}
}

export function lerUltimaAtividade(sessaoId) {
  try {
    const v = localStorage.getItem(CHAVE_ATIVIDADE);
    if (!v) return null;

    // Formato antigo: um numero puro, sem dono. Esta gravado no navegador de
    // todo mundo que ja usou o sistema. Quem pergunta por uma sessao especifica
    // NAO pode aceita-lo -- foi exatamente essa marca orfa que prendeu o Kaio do
    // lado de fora. Sem dono na pergunta (testes da regra pura), vale como antes.
    if (v[0] !== '{') {
      if (sessaoId !== undefined) return null;
      const n = Number(v);
      return Number.isFinite(n) ? n : null;
    }

    const { t, s } = JSON.parse(v);
    if (!Number.isFinite(t)) return null;
    if (sessaoId !== undefined && s !== sessaoId) return null;
    return t;
  } catch (e) { return null; }
}

export function limparAtividade() {
  try { localStorage.removeItem(CHAVE_ATIVIDADE); } catch (e) {}
}

// ---- Motivo do ultimo logout, para a tela de login explicar ----
// sessionStorage (nao localStorage): o aviso vale so para esta aba/retorno
// imediato, nao fica marcado para sempre.
export function marcarLogoutPorInatividade() {
  try { sessionStorage.setItem(CHAVE_MOTIVO, 'inatividade'); } catch (e) {}
}

export function consumirMotivoLogout() {
  try {
    const v = sessionStorage.getItem(CHAVE_MOTIVO);
    if (v) sessionStorage.removeItem(CHAVE_MOTIVO);
    return v;
  } catch (e) { return null; }
}

// Eventos que contam como "usuario presente". passive: nao bloqueiam a rolagem.
const EVENTOS = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll', 'wheel', 'focus'];

// ---- Hook que liga o monitor enquanto houver sessao ----
// - grava a atividade (com throttle) a cada evento;
// - verifica de segundo em segundo (e ao voltar o foco/aba);
// - 'expirado' chama onExpira uma vez; 'avisar' liga o modal com contagem.
// Devolve { avisando, restanteMs, continuar } para o modal desenhar.
export function useMonitorInatividade({ ativo, onExpira, opcoes }) {
  // Limite do APARELHO, escolhido uma vez por montagem (nao muda se a pessoa trocar de ponteiro no meio da sessao). `opcoes.limite` vence.
  const [limiteDoAparelhoNaMontagem] = React.useState(limiteDoAparelho);
  const [avisando, setAvisando] = React.useState(false);
  const [restanteMs, setRestanteMs] = React.useState(opcoes?.limite ?? limiteDoAparelhoNaMontagem);
  // onExpira num ref para nao re-assinar eventos a cada render do pai.
  const onExpiraRef = React.useRef(onExpira);
  onExpiraRef.current = onExpira;

  const limite = opcoes?.limite ?? limiteDoAparelhoNaMontagem;
  const aviso = opcoes?.aviso ?? AVISO_MS;
  // Identificador da sessao vigente. `undefined` (nenhum informado) mantem o
  // comportamento antigo, que os testes da regra pura usam.
  const sessaoId = opcoes?.sessaoId;

  React.useEffect(() => {
    if (!ativo) { setAvisando(false); return; }
    // Comeca o relogio se ainda nao ha marca DESTA sessao (login novo). A marca
    // de uma sessao anterior nao conta -- ver o comentario em marcarAtividade.
    if (lerUltimaAtividade(sessaoId) == null) marcarAtividade(Date.now(), sessaoId);

    let ultimoRegistro = 0;
    let expirou = false;
    const registrar = () => {
      const agora = Date.now();
      // Throttle: no maximo uma escrita a cada 5s -- nao martela o localStorage
      // a cada pixel de mouse.
      if (agora - ultimoRegistro > 5000) {
        // Uma sessao que JA VENCEU nao se renova por um evento atrasado. Pagina congelada por dias (celular no bolso, notebook suspenso): ao voltar,
        // focus/scroll/mousemove podem chegar ANTES de a verificacao rodar, e gravar a marca aqui reviveria a sessao por mais um prazo inteiro, de
        // novo e de novo. Se ja venceu, quem decide e a verificacao (que expira). Reproduzido na revisao final de 27/10.
        if (avaliarInatividade(agora, lerUltimaAtividade(sessaoId), { limite, aviso }) === 'expirado') { verificar(); return; }
        ultimoRegistro = agora;
        marcarAtividade(agora, sessaoId);
      }
    };

    const verificar = () => {
      if (expirou) return;
      const agora = Date.now();
      // O `sessaoId` aqui e coerencia, nao guarda testada: a linha de montagem
      // acima ja substituiu qualquer marca de outra sessao, entao num navegador
      // so esta leitura daria o mesmo resultado sem o dono. Tirar nao quebra
      // nenhum teste -- conferido por mutacao em 15/09. Fica porque ler com um
      // criterio e gravar com outro e como divergencia comeca.
      const ultima = lerUltimaAtividade(sessaoId);
      const estado = avaliarInatividade(agora, ultima, { limite, aviso });
      if (estado === 'expirado') {
        expirou = true;
        setAvisando(false);
        onExpiraRef.current && onExpiraRef.current();
        return;
      }
      if (estado === 'avisar') {
        setAvisando(true);
        setRestanteMs(Math.max(0, limite - (agora - (ultima ?? agora))));
      } else {
        setAvisando(false);
      }
    };

    EVENTOS.forEach((e) => window.addEventListener(e, registrar, { passive: true }));
    document.addEventListener('visibilitychange', verificar);
    // Imediato: cobre o navegador reaberto depois de horas.
    verificar();
    const id = setInterval(verificar, 1000);

    return () => {
      EVENTOS.forEach((e) => window.removeEventListener(e, registrar));
      document.removeEventListener('visibilitychange', verificar);
      clearInterval(id);
    };
  // Re-liga quando 'ativo' muda (entrar/sair da sessao) e quando a SESSAO muda
  // -- trocar de conta sem passar pela tela de login tem de reiniciar o relogio.
  // limite e aviso nao entram: o limite e escolhido UMA vez por montagem (estado
  // do hook) e o aviso e constante de modulo, entao nao ha o que re-assinar. O
  // teste "o limite da montagem vale tambem quando a sessao muda" prova isso.
  // Um `opcoes.limite` que mudasse no meio da vida do componente tambem seria
  // ignorado ate `ativo` ou `sessaoId` mudarem; nenhum chamador passa `opcoes.limite`.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ativo, sessaoId]);

  // "Continuar conectado": marca atividade agora e fecha o aviso.
  //
  // O `sessaoId` aqui NAO e detalhe: sem ele a marca sairia sem dono, `ler`
  // devolveria null para sempre, e o logout por inatividade ficaria desligado em
  // silencio a partir do primeiro "continuar" -- um recurso de seguranca morto
  // sem ninguem notar.
  const continuar = React.useCallback(() => { marcarAtividade(Date.now(), sessaoId); setAvisando(false); }, [sessaoId]);

  return { avisando, restanteMs, continuar };
}
